import React, { useState, useEffect, useRef } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import {
  collection, addDoc, deleteDoc, doc, onSnapshot,
  serverTimestamp, setDoc, getDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, googleProvider, db, storage } from './firebase';
import './Admin.css';

const OWNER_EMAIL = 'elmetacl@gmail.com';

const IMAGE_SLOTS = [
  { key: 'mainWinrates',  label: 'Winrates (página Inicio)',       fallback: '/merged_images.png' },
  { key: 'metaMatchups',  label: 'Tabla de matchups (página Meta)', fallback: '/vs_beta_2.png'     },
  { key: 'cardCrear',     label: 'Fondo card Construir Mazo',       fallback: '/crear.png'          },
  { key: 'cardMeta',      label: 'Fondo card Meta',                 fallback: '/meta.png'           },
  { key: 'cardArticulos', label: 'Fondo card Artículos',            fallback: '/articulos.png'      },
];

function Admin() {
  const [user, setUser]       = useState(undefined); // undefined = cargando
  const [tab, setTab]         = useState('articulos');
  const [articulos, setArticulos] = useState([]);
  const [imageConfig, setImageConfig] = useState({});
  const [form, setForm]       = useState({ titulo: '', contenido: '', autor: 'ElMeta.cl' });
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState({});
  const fileRefs = useRef({});

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u));
    return () => unsub();
  }, []);

  // Cargar artículos en tiempo real
  useEffect(() => {
    if (!user || user.email !== OWNER_EMAIL) return;
    const unsub = onSnapshot(collection(db, 'articles'), (snap) => {
      const data = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setArticulos(data);
    });
    return () => unsub();
  }, [user]);

  // Cargar config de imágenes
  useEffect(() => {
    if (!user || user.email !== OWNER_EMAIL) return;
    getDoc(doc(db, 'config', 'images')).then((snap) => {
      if (snap.exists()) setImageConfig(snap.data());
    });
  }, [user]);

  const handleLogin  = () => signInWithPopup(auth, googleProvider);
  const handleLogout = () => signOut(auth);

  const handleAddArticulo = async (e) => {
    e.preventDefault();
    await addDoc(collection(db, 'articles'), {
      ...form,
      fecha: new Date().toLocaleDateString('es-CL'),
      createdAt: serverTimestamp(),
    });
    setForm({ titulo: '', contenido: '', autor: 'ElMeta.cl' });
    setShowForm(false);
  };

  const handleDeleteArticulo = async (id) => {
    if (!window.confirm('¿Eliminar este artículo?')) return;
    await deleteDoc(doc(db, 'articles', id));
  };

  const handleImageUpload = async (slotKey, file) => {
    if (!file) return;
    setUploading((prev) => ({ ...prev, [slotKey]: true }));
    try {
      const storageRef = ref(storage, `images/${slotKey}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const updated = { ...imageConfig, [slotKey]: url };
      await setDoc(doc(db, 'config', 'images'), updated);
      setImageConfig(updated);
    } finally {
      setUploading((prev) => ({ ...prev, [slotKey]: false }));
    }
  };

  // --- Estados de carga / acceso ---
  if (user === undefined) {
    return <div className="admin-center"><p>Cargando...</p></div>;
  }

  if (!user) {
    return (
      <div className="admin-center">
        <div className="admin-login-box">
          <h1>Panel de administración</h1>
          <button className="admin-btn-google" onClick={handleLogin}>
            <i className="fa-brands fa-google" /> Iniciar sesión con Google
          </button>
        </div>
      </div>
    );
  }

  if (user.email !== OWNER_EMAIL) {
    return (
      <div className="admin-center">
        <div className="admin-login-box">
          <p>Sin acceso.</p>
          <button className="admin-btn-secondary" onClick={handleLogout}>Cerrar sesión</button>
        </div>
      </div>
    );
  }

  // --- Panel principal ---
  return (
    <div className="Admin">
      <header className="admin-header">
        <span className="admin-title">Admin — ElMeta.cl</span>
        <button className="admin-btn-secondary" onClick={handleLogout}>
          <i className="fa-solid fa-right-from-bracket" /> Salir
        </button>
      </header>

      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'articulos' ? 'active' : ''}`} onClick={() => setTab('articulos')}>
          Artículos
        </button>
        <button className={`admin-tab ${tab === 'imagenes' ? 'active' : ''}`} onClick={() => setTab('imagenes')}>
          Imágenes
        </button>
      </div>

      <main className="admin-main">

        {/* ---- TAB ARTÍCULOS ---- */}
        {tab === 'articulos' && (
          <div className="admin-section">
            <div className="admin-section-header">
              <h2>Artículos</h2>
              <button className="admin-btn-primary" onClick={() => setShowForm(true)}>
                <i className="fa-solid fa-plus" /> Nuevo
              </button>
            </div>

            {showForm && (
              <form className="admin-form" onSubmit={handleAddArticulo}>
                <h3>Nuevo artículo</h3>
                <label>Título</label>
                <input required value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} placeholder="Título" />
                <label>Autor</label>
                <input value={form.autor} onChange={(e) => setForm({ ...form, autor: e.target.value })} />
                <label>Contenido</label>
                <textarea required rows={12} value={form.contenido} onChange={(e) => setForm({ ...form, contenido: e.target.value })} placeholder="Contenido del artículo..." />
                <div className="admin-form-actions">
                  <button type="button" className="admin-btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
                  <button type="submit" className="admin-btn-primary">Publicar</button>
                </div>
              </form>
            )}

            <div className="admin-list">
              {articulos.length === 0 && <p className="admin-empty">No hay artículos.</p>}
              {articulos.map((art) => (
                <div key={art.id} className="admin-list-item">
                  <div>
                    <strong>{art.titulo}</strong>
                    <span className="admin-list-meta">{art.autor} · {art.fecha}</span>
                  </div>
                  <button className="admin-btn-delete" onClick={() => handleDeleteArticulo(art.id)}>
                    <i className="fa-solid fa-trash" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ---- TAB IMÁGENES ---- */}
        {tab === 'imagenes' && (
          <div className="admin-section">
            <h2>Imágenes</h2>
            <p className="admin-hint">Sube una nueva imagen para reemplazar la que se muestra en el sitio.</p>
            <div className="admin-image-grid">
              {IMAGE_SLOTS.map(({ key, label, fallback }) => (
                <div key={key} className="admin-image-slot">
                  <p className="admin-image-label">{label}</p>
                  <img
                    src={imageConfig[key] || fallback}
                    alt={label}
                    className="admin-image-preview"
                  />
                  <input
                    type="file"
                    accept="image/*"
                    ref={(el) => (fileRefs.current[key] = el)}
                    style={{ display: 'none' }}
                    onChange={(e) => handleImageUpload(key, e.target.files[0])}
                  />
                  <button
                    className="admin-btn-primary"
                    disabled={uploading[key]}
                    onClick={() => fileRefs.current[key]?.click()}
                  >
                    {uploading[key] ? 'Subiendo...' : 'Cambiar imagen'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default Admin;
